import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { logErrorToSentry } from "@litres/utils";
import type {
  IApiError,
  IVarioqubTests,
  IStandardGUJRequestParams,
  IResponseRedirect,
  IReduxRequestNode,
  TGetPresentBookNode,
  IPresentBookRequest,
  IPresentBookResponse,
  TGetUserGiftsNode,
  IUserGiftsRequest,
  IUserGiftsResponse,
  IProceedPaymentResponse,
  ABTestSerializer,
  OkResponse_list_ABTestSerializer__ as ABTestResponse,
  ShortAbonementSerializer,
  ShortUserAccountSerializer,
  ShortUserLibraryInfoSerializer,
  ShortLitResSubscriptionSerializer,
  ShortUserProfileSerializer,
  ShortUserSerializerWithCustomSocials,
  ShortUserSettings,
  UserPartnerSubscriptionsSerializer,
  UserSocialSerializerWithCustomProviderCode,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import { getGlobalHeaders } from "../browser/browserSlice";
import { foundationUserApi } from "../../rtkq/foundationUser/foundationUser.api";
import { varioqubApi } from "../../rtkq/varioqub/varioqub.api";
import type { RootState } from "../../store";
import { addHydrateReducer } from "../../utils/hydrateReducer";
import type { IVarioqubResponse } from "../../rtkq/varioqub/varioqub.types";

export interface IFoundationUserState
  extends ShortUserSerializerWithCustomSocials {
  SID?: string;
  isLoading: boolean;
  isOfficeUser: boolean;
  error?: Record<string, unknown> | IApiError;
  isAsking: boolean;
  BSK: string[];
  LKS: string[];
  actions: {
    teleport_CSRF: TTeleportCSRFNode;
  };
  safemode_active_step?: "confirm" | "success" | "close";
  varioqubTests: IVarioqubTests;
}

const initialState: IFoundationUserState = {
  id: 0,
  login: "",
  BSK: [],
  LKS: [],
  is_staff: false,
  isOfficeUser: false,
  profile: {
    has_folder_arts: false,
  },
  account: {
    display: 0,
    real: 0,
    full: 0,
    bonus: 0,
  },
  settings: {
    profile_privacy: "default",
    is_safemode_enabled: null,
  },
  socials: [],
  library_info: {
    title: null,
    logo: {},
    host: null,
    is_school_user: false,
  },
  abonement: {
    id: null,
    is_active: false,
    price: null,
    rebill_id: null,
    valid_till: null,
    grace_period_start: null,
    gift_arts_refresh_at: null,
    payment_system_id: null,
    purchase_origin: null,
  },
  subscription: {
    id: null,
    grace_period_start: null,
    rebill_id: null,
    payment_system_id: null,
    purchase_origin: null,
  },
  partner_subscriptions: {
    has_active: false,
    subscriptions: [],
  },
  phone: {},
  ab_tests: [],
  isLoading: false,
  error: {},
  isAsking: false,
  actions: {
    teleport_CSRF: {},
  },
  varioqubTests: {
    ymabParam: "",
    flags: {},
    testIds: [],
    initialized: false,
    initializingError: false,
  },
  loyalty: {
    is_loyalty_user: false,
    bonuses_info: null,
  },
  is_tester: false,
};

interface IGetABTestsRequest {
  SID: string;
}

export interface TForceABTestParams {
  test: number;
  state: 0 | 1 | 2 | 3;
}
export type TForceABTestRequest = TForceABTestParams &
  IStandardGUJRequestParams;
export interface TForceABTestResponse extends Record<string, unknown> {
  success: string;
}

interface ITeleportGUJParams {
  host: string;
  new_page?: string;
}

interface ITeleportGUJResponse {
  status: string;
  redirect: IResponseRedirect;
  csrf: string;
}

type ITeleportGUJRequest = IStandardGUJRequestParams & ITeleportGUJParams;
export type TTeleportCSRFNode = IReduxRequestNode<
  ITeleportGUJRequest,
  ITeleportGUJResponse
>;

/**
 * Получение токена и возврат ссылки на библиотеку
 */
export const teleport_CSRF = createAsyncThunk(
  "foundationUser/teleport_CSRF",
  async (params: { data: ITeleportGUJParams }) => {
    const response = new Request.CSRF<
      ITeleportGUJRequest,
      ITeleportGUJResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "teleport_CSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "teleport",
          gu_ajax: true,
          ...params.data,
        },
      },
    });

    return await response.fetch();
  }
);

interface IReadReportParams {
  art: number;
  h: number;
  p: number;
  t?: 0 | 1;
  f?: 0 | 1;
  n?: number;
}

/**
 * Вызовет Catalit API и отрепортит о прослушенном прогрессе аудиокниги / прочтении книги
 * @param { number } art - ID книги
 * @param { number } p - промилле позиции чтения (как процент 1/1000). Желательно передавать «крайнюю точку». Например, пользователь прочитал страницу, перелистнул, в отчет надо отправить промилле с позицией чтения начала следующей страницы (той, которую он только будет читать);
 * @param { number } h - количество прочитанных символов. Считаются буквы, пробелы, знаки препинания. Например, в предложении «Слово – серебро, молчание – золото» 34 символа. Может принимать значение 0. Это значит что фактически чтения не было, было пролистывание. Пролистыванием считается либо явная перемотка текста посредством скроллера, либо чтение/пролистывание со скоростью выше 7000 символов/минуту. Если это аудиокнига, то в параметре h отправляем прослушанные десятки секунд. За единицу времени считаем 10 секунд, даже если пользователь прослушал 1 секунду, все равно передаем 10 (1-9сек отправляем - 10, если прослушал до 20сек, то передаем 10 следующих.).
 * @param { 0 | 1 | undefined } t - признак, что пользователь читает фрагмент (при этом f не передаётся). Параметр может принимать значения:
 * 0 – признак не выполняется, значит пользователь читает полный текст. Если параметр t отсутствует, по умолчанию считаем, что он принимает значение 0;
 * 1 – пользователь читает фрагмент;
 * @param { 0 | 1 | undefined } f - признак, что книга читается/слушается по подписке, за рекламу и т. п. То есть это не «паблик», не совсем бесплатная книга, но в то же время за конкретно эту книгу пользователь не платил. Вопрос, является ли книга «пабликом», решается на бэкенде проверкой basket.real_payed для данного пользователя и данной книги. Если там значение 0 (или записи в basket нет, что вообще-то баг), то считается, что книга читается даром, даже не за рекламу. Параметр может принимать значения 0 или 1
 * @param { number | undefined } n - число событий. Актуально при h=0. Например, «перелистнул, не читая 6 раз», «перематывал книгу скроллером с 28-ю остановками по 0,5 с и более, потом отпустил» – будет 6 и 28, соответственно, при h=0. По каждому типу событий отправляется отдельный отчет. Параметр n не может быть использован при значении параметра h > 0
 */
export const readReport = createAsyncThunk(
  "foundationUser/readReport",
  async (params: IReadReportParams) => {
    const response = new Request.Cat2<IReadReportParams, any>({
      url: "/",
      provider: {
        func: "w_read_report",
        id: "w_read_report",
      },
      request: {
        method: "POST",
        params: params,
      },
    });

    return await response.fetch();
  }
);

interface IRequestBiblioArt {
  art: number;
  strict?: 1;
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Вызовет Catalit API и запросит/возьмет книгу в библиотеке
 * @param { number } art - ID книги
 * @param { 1 | undefined } strict - наличие этого признака, равного единице, включает на сервере проверку на количество запросов от пользователя. Если запросов больше 15 - будет отображаться соответствующая ошибка
 */
export const requestBiblioArt = createAsyncThunk(
  "foundation/requestBiblioArt",
  async (params: IRequestBiblioArt) => {
    const response = new Request.Cat2<IRequestBiblioArt, any>({
      url: "/",
      provider: {
        func: "w_biblio_request",
        id: "w_biblio_request_art",
      },
      request: {
        method: "POST",
        params: { art: params.art, strict: params.strict },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface ICancelRequestBiblioArt {
  art: number;
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Вызовет Catalit API и отменит запрос за книгой в библиотеке
 * @param { number } art - ID книги
 */
export const cancelRequestBiblioArt = createAsyncThunk(
  "foundation/cancelRequestBiblioArt",
  async (params: ICancelRequestBiblioArt) => {
    const response = new Request.Cat2<ICancelRequestBiblioArt, any>({
      url: "/",
      provider: {
        func: "w_cancel_biblio_request",
        id: "cancel_biblio_request",
      },
      request: {
        method: "POST",
        params: { art: params.art },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

/**
 * Взятие арта по абонементу/подписке
 */
export const getPresentBook_CSRF = createAsyncThunk(
  "foundationUser/getPresentBook",
  async (params: {
    artId: number;
    subId: number;
    onSuccess?: (success: TGetPresentBookNode) => void;
    onError?: (error: TGetPresentBookNode) => void;
  }) => {
    const response = new Request.CSRF<
      IPresentBookRequest,
      IPresentBookResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "getPresentBook",
      },
      request: {
        method: "POST",
        data: {
          action: "get_present_book",
          art: params.artId,
          subscription: params.subId,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

/**
 * Взятие подарочного арта
 */
export const decrUserGifts_CSRF = createAsyncThunk(
  "foundationUser/decrUserGifts",
  async (params: {
    artId: number;
    onSuccess?: (success: TGetUserGiftsNode) => void;
    onError?: (error: TGetUserGiftsNode) => void;
  }) => {
    const response = new Request.CSRF<IUserGiftsRequest, IUserGiftsResponse>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "decrUserGifts",
      },
      request: {
        method: "POST",
        data: {
          action: "decr_user_gifts",
          art: params.artId,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IProceedPaymentRequest {
  action: string;
  descr: number;
  custom_set: number;
  csrf: string;
  ajax: boolean;
  js: number;
  gu_ajax: boolean;
}

export const proceedPayment = createAsyncThunk(
  "foundationUser/proceedPayment",
  async (params: {
    customSet: number;
    csrf: string;
    onSuccess?: (success: IProceedPaymentResponse) => void;
    onError?: () => void;
  }) => {
    const response = new Request.CSRF<
      IProceedPaymentRequest,
      IProceedPaymentResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "proceedPayment",
      },
      request: {
        method: "POST",
        data: {
          action: "proceed_payment",
          descr: 91,
          custom_set: params.customSet,
          csrf: params.csrf,
          ajax: true,
          js: 1,
          gu_ajax: true,
        },
      },
      onSuccess: params.onSuccess,
      onError: error => {
        //логируем ошибку ручки в SENTRY, 500-ки или 400-rb
        logErrorToSentry("proceed_payment is failed", {
          contextProperties: ["params", { error: JSON.stringify(error) }],
          tags: {
            type: "critical",
          },
          logLevel: "fatal",
        });
        if (params.onError) params.onError();
      },
    });

    return await response.fetch();
  }
);

export const getABTests = createAsyncThunk(
  "foundationUser/getABTests",
  async ({ SID }: IGetABTestsRequest, { getState }) => {
    const response = new Request.Foundation<IGetABTestsRequest, ABTestResponse>(
      {
        url: `/api/ab-tests`,
        request: {
          method: "POST",
        },
        provider: { id: "getABTests" },
        headers: {
          ...getGlobalHeaders(getState() as RootState),
          "Session-Id": SID,
        },
      }
    );
    return await response.fetch();
  }
);

export const foundationUserSlice = createSlice({
  name: "foundationUser",
  initialState,
  reducers: {
    setUserSID: (
      state: IFoundationUserState,
      action: PayloadAction<{ SID: string }>
    ) => {
      state.SID = action.payload.SID;
    },
    setUserID: (
      state: IFoundationUserState,
      action: PayloadAction<{ id: number }>
    ) => {
      state.id = action.payload.id;
    },
    setFoundationUserLogin: (
      state: IFoundationUserState,
      action: PayloadAction<{ login: string }>
    ) => {
      state.login = action.payload.login;
    },
    setFoundationUserProfile: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<ShortUserProfileSerializer>>
    ) => {
      state.profile = { ...state.profile, ...action.payload };
    },
    setFoundationUserBalance: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<ShortUserAccountSerializer>>
    ) => {
      state.account = { ...state.account, ...action.payload };
    },
    setCustomCookieValue: (
      state: IFoundationUserState,
      action: PayloadAction<{ BSK?: string[]; LKS?: string[] }>
    ) => {
      if (Array.isArray(action.payload.BSK)) {
        state.BSK = action.payload.BSK.filter(v => !!v);
      }
      if (Array.isArray(action.payload.LKS)) {
        state.LKS = action.payload.LKS.filter(v => !!v);
      }
    },
    setFoundationUserLibrary: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<ShortUserLibraryInfoSerializer>>
    ) => {
      state.library_info = { ...state.library_info, ...action.payload };
    },
    setFoundationUserAccountSettings: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<ShortUserSettings>>
    ) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    setFoundationUserSafemodeStep: (
      state: IFoundationUserState,
      action: PayloadAction<IFoundationUserState["safemode_active_step"]>
    ) => {
      state.safemode_active_step = action.payload;
    },
    setFoundationUserSocnets: (
      state: IFoundationUserState,
      action: PayloadAction<UserSocialSerializerWithCustomProviderCode[]>
    ) => {
      state.socials = action.payload;
    },
    setFoundationUserAbonement: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<ShortAbonementSerializer>>
    ) => {
      state.abonement = { ...state.abonement, ...action.payload };
    },
    setFoundationUserPartnersSubscriptions: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<UserPartnerSubscriptionsSerializer>>
    ) => {
      state.partner_subscriptions = {
        ...state.partner_subscriptions,
        ...action.payload,
      };
    },
    setFoundationUserSubscription: (
      state: IFoundationUserState,
      action: PayloadAction<Partial<ShortLitResSubscriptionSerializer>>
    ) => {
      state.subscription = { ...state.subscription, ...action.payload };
    },
    setFoundationUserAvatar: (
      state: IFoundationUserState,
      action: PayloadAction<string>
    ) => {
      state.profile.avatar_url = action.payload;
    },
    setFoundationUserABTests: (
      state: IFoundationUserState,
      action: PayloadAction<ABTestSerializer[]>
    ) => {
      state.ab_tests = action.payload;
    },
    setIsOfficeUser: (
      state: IFoundationUserState,
      action: PayloadAction<boolean>
    ) => {
      state.isOfficeUser = action.payload;
    },
    setVarioqubTests: (
      state: IFoundationUserState,
      action: PayloadAction<
        ({ isForCache: boolean } & IVarioqubResponse) | Partial<IVarioqubTests>
      >
    ) => {
      // сужение типа до IVarioqubResponse
      if ("i" in action.payload) {
        const flags = action.payload.flags.reduce<Record<string, string>>(
          (acc, flagData) => ({
            ...acc,
            [flagData.n]: flagData.v,
          }),
          {}
        );

        state.varioqubTests = {
          ...state.varioqubTests,
          flags,
          ymabParam: action.payload.isForCache ? "" : action.payload.i,
          testIds: action.payload.testids ?? [],
          initialized: true,
        };
      } else {
        state.varioqubTests = { ...state.varioqubTests, ...action.payload };
      }
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "foundationUser");
    /**
     * Обработка загрузки и ответа от teleport
     */
    builder.addCase(teleport_CSRF.pending, (state: IFoundationUserState) => {
      state.actions.teleport_CSRF.isLoading = true;
      state.actions.teleport_CSRF.error = undefined;
    });
    builder.addCase(
      teleport_CSRF.fulfilled,
      (
        state: IFoundationUserState,
        action: PayloadAction<TTeleportCSRFNode>
      ) => {
        state.actions.teleport_CSRF.isLoading = false;
        state.actions.teleport_CSRF = action.payload;

        if (action.payload.error) {
          state.actions.teleport_CSRF.error = action.payload.error;
          return;
        }
      }
    );

    builder.addMatcher(
      foundationUserApi.endpoints.getUserDataForSSR.matchFulfilled,
      (state, action) => {
        return {
          ...state,
          ...action.payload,
          error: {},
          isLoading: false,
          isAsking: false,
          BSK: state.BSK,
          LKS: state.LKS,
          actions: {
            teleport_CSRF: {},
          },
          ab_tests: state.ab_tests,
        };
      }
    );

    builder.addMatcher(
      foundationUserApi.endpoints.getAbTests.matchFulfilled,
      (state, action) => {
        state.ab_tests = action.payload;
      }
    );

    builder.addMatcher(
      varioqubApi.endpoints.getVarioqubData.matchFulfilled,
      (state, { payload }) => {
        const flags = payload.flags.reduce<Record<string, string>>(
          (acc, flagData) => ({
            ...acc,
            [flagData.n]: flagData.v,
          }),
          {}
        );
        state.varioqubTests = {
          ...state.varioqubTests,
          flags,
          ymabParam: payload.i,
          testIds: payload.testids ?? [],
          initialized: true,
        };
      }
    );

    builder.addMatcher(
      varioqubApi.endpoints.getVarioqubData.matchRejected,
      state => {
        state.varioqubTests = {
          ...state.varioqubTests,
          initializingError: true,
        };
      }
    );
  },
});

export const {
  setUserSID,
  setUserID,
  setFoundationUserLogin,
  setFoundationUserProfile,
  setFoundationUserBalance,
  setFoundationUserLibrary,
  setFoundationUserAccountSettings,
  setFoundationUserSocnets,
  setFoundationUserPartnersSubscriptions,
  setFoundationUserSubscription,
  setFoundationUserAvatar,
  setFoundationUserSafemodeStep,
  setFoundationUserABTests,
  setIsOfficeUser,
  setCustomCookieValue,
  setVarioqubTests,
  setFoundationUserAbonement,
} = foundationUserSlice.actions;

export default foundationUserSlice.reducer;
